import { useState } from 'react';
import { subYears } from 'date-fns';
import { CalendarIcon, ChevronDown } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  DATE_RANGE_PRESETS,
  DATE_RANGE_PRESET_LABELS,
  type DateRangePreset,
  type DateRangeValue,
  dateRangeLabel,
  fromIsoDate,
  resolveDateRange,
  toIsoDate,
} from '@/lib/date-range';

/**
 * A date-range control: named calendar periods down the left, a two-month
 * range calendar on the right.
 *
 * The periods are the point. "Last quarter" is a rule that re-answers itself
 * as time passes, which a pair of typed dates cannot do — so the selection
 * stores the period and resolves the window on read (see `@/lib/date-range`).
 * Picking a period applies it and closes; dragging on the calendar is by
 * definition no longer a named period, so it switches the selection to
 * Custom.
 *
 * The calendar caption is month + year DROPDOWNS rather than arrows. A range
 * near today is a few clicks either way, but reaching a month two years back
 * an arrow at a time is the difference between possible and possible in
 * practice — and this control is often the only way to name a date.
 *
 * NOTE: `components/dashboard/date-range-filter.tsx` is a second, older
 * range picker offering rolling windows ("Last 7 days", "Last 30 days")
 * rather than calendar periods. It still backs the dashboard's metric
 * period; this one is the general-purpose control.
 */
export interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
  /** Periods to offer, in order. Defaults to all of them. */
  presets?: readonly DateRangePreset[];
  /**
   * Bounds the year dropdown can reach. `latest` defaults to today because
   * every caller so far is looking backwards; pass it explicitly for a
   * control that should reach into the future.
   */
  earliest?: Date;
  latest?: Date;
  /** Extra classes on the trigger button. */
  className?: string;
  id?: string;
}

export function DateRangePicker({
  value,
  onChange,
  presets = DATE_RANGE_PRESETS,
  earliest,
  latest,
  className,
  id,
}: DateRangePickerProps): React.ReactElement {
  const [open, setOpen] = useState(false);

  const today = new Date();
  const startMonth = earliest ?? subYears(today, 5);
  const endMonth = latest ?? today;

  const resolved = resolveDateRange(value);

  // The calendar highlights the resolved window — named periods included, so
  // picking "Last month" shows you which month that is — or the in-progress
  // custom pick. "All time" is no window, so nothing is highlighted.
  const selected: DateRange | undefined = resolved
    ? { from: fromIsoDate(resolved.from), to: fromIsoDate(resolved.to) }
    : value.preset === 'custom'
      ? { from: fromIsoDate(value.from), to: fromIsoDate(value.to) }
      : undefined;

  const pickPreset = (preset: DateRangePreset): void => {
    if (preset === 'custom') {
      // Seed Custom with the window already on screen, so the calendar
      // doesn't blank out the moment the user asks to adjust it by hand.
      onChange({
        preset: 'custom',
        from: resolved?.from ?? value.from,
        to: resolved?.to ?? value.to,
      });
      // Stay open: the user still has to pick the dates.
      return;
    }
    onChange({ preset });
    setOpen(false);
  };

  const pickRange = (range: DateRange | undefined): void => {
    onChange({
      preset: 'custom',
      from: range?.from ? toIsoDate(range.from) : undefined,
      to: range?.to ? toIsoDate(range.to) : undefined,
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          size="sm"
          className={cn('h-8 justify-start gap-2 font-normal', className)}
        >
          <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="truncate">{dateRangeLabel(value)}</span>
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          <div className="w-36 space-y-1 border-r p-2">
            {presets.map((preset) => (
              <Button
                key={preset}
                type="button"
                variant={value.preset === preset ? 'secondary' : 'ghost'}
                size="sm"
                className="w-full justify-start font-normal"
                onClick={() => pickPreset(preset)}
              >
                {DATE_RANGE_PRESET_LABELS[preset]}
              </Button>
            ))}
          </div>
          <Calendar
            mode="range"
            numberOfMonths={2}
            showOutsideDays={false}
            captionLayout="dropdown"
            startMonth={startMonth}
            endMonth={endMonth}
            defaultMonth={selected?.from ?? endMonth}
            selected={selected}
            onSelect={pickRange}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
